<script lang="ts">
import { defineComponent, PropType, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { UnitType } from '@interfaces/unit_type';
import type { StructureType } from '@interfaces/structure_type';
import useUnitLogic from '../../../composables/useUnitLogic';
import { useLocalizedNamesStore } from '../../../stores/localizedNames';

export default defineComponent({
  name: 'Unit',
  props: {
    unit: {
      type: Object as PropType<UnitType>,
      required: true
    },
    structure: {
      type: Object as PropType<StructureType>,
      required: true
    }
  },
  setup(props) {
    const i18n = useI18n();
    const namesStore = useLocalizedNamesStore();

    const {
      selected_units,
      selected_uniq_keys,
      max_quantity,
      available_quantity,
    } = useUnitLogic(props.structure);

    const selectedQuantity = computed(() =>
      selected_units.value.find(item => item.tech_name === props.unit.tech_name)?.quantity || 0
    );

    const availableQuantity = computed(() =>
      max_quantity(props.unit) - selectedQuantity.value
    );

    const isUniqAvailable = computed(() =>
      !props.unit.uniq_key || !selected_uniq_keys.value.includes(props.unit.uniq_key)
    );

    const isAvailable = computed(() =>
      availableQuantity.value > 0 && isUniqAvailable.value
    );

    const unitName = computed(() => {
      const localized = namesStore.getName(
        'unit',
        props.unit.tech_name || undefined,
        props.unit.id || undefined
      );
      if (localized) return localized;

      const locale = i18n.locale.value;
      if (locale === 'en') return props.unit.name_en || props.unit.name_ru || props.unit.name || '';
      return props.unit.name_ru || props.unit.name || '';
    });

    return {
      selectedQuantity,
      availableQuantity,
      isUniqAvailable,
      isAvailable,
      available_quantity,
      t: i18n.t,
      unitName
    };
  }
});
</script>

<template>
  <div class="unit shadow-sm" :class="{'disabled': !isAvailable}">
    <b>{{ unitName }}</b>
    <div @click="$emit('open-unit-modal', unit)" class="unit-image">
      <img v-image-fallback :src="unit.image_path" class="image d-block"/>
    </div>

    <p class="choose_quantity">
      <button
        type="button"
        class="substract"
        @click.prevent.stop="$emit('subtract-unit', unit)"
        :disabled="selectedQuantity === 0"
      >-</button>
      <span class="current">
        {{ unit.point_cost }}
      </span>
      <button
        type="button"
        class="add"
        @click.prevent.stop="$emit('add-unit', unit)"
        :disabled="available_quantity(unit) === 0 || !isUniqAvailable"
      >+</button>
    </p>

    <p class="max_quantity">
      {{ t('units.available') }}:&nbsp;<span>{{ available_quantity(unit) }}</span>
    </p>
  </div>
</template>
